import moment from 'moment';
import numeral from 'numeral';

/**
 * Search Console's sitemap records, read into the rows of the four blocks.
 *
 * A record is what was submitted and what Google did with the submission — never an index count.
 * Two rules run through every row built here:
 *
 * - A count Google did not return is null, shown as "Not returned", and only a returned "0" is a
 *   zero. A pending sitemap often has no warning or error count yet; reading that as "0 errors"
 *   would report a clean bill of health nobody issued.
 * - Only `contents[].submitted` is read. `contents[].indexed` is deprecated by Google and no longer
 *   meaningful, and an indexed percentage built from it — or from the submitted counts against
 *   anything — would be a figure Search Console does not stand behind.
 *
 * Times are shown as YYYY-MM-DD HH:mm in the viewer's clock, which also keeps them sortable as text;
 * the raw RFC 3339 value stays in the selected record's details and in the export.
 */

const TIME_FORMAT = 'YYYY-MM-DD HH:mm';

/** The record filter's views, over the records already returned. */
export const FILTERS = ['all', 'issues', 'pending', 'indexes'];

/** Sitemap and content types Search Console documents; anything else is shown as it arrived. */
const SITEMAP_TYPES = ['sitemap', 'rssFeed', 'atomFeed', 'urlList', 'patternSitemap', 'notSitemap'];
const CONTENT_TYPES = [
    'web',
    'image',
    'video',
    'news',
    'mobile',
    'androidApp',
    'iosApp',
    'pattern',
    'dataFeedElement',
];

export function formatTime(value) {
    return value ? moment(value).format(TIME_FORMAT) : null;
}

export function formatCount(value) {
    return value === null ? null : numeral(value).format('0,0');
}

/** A count as returned (the API sends int64 as text), or null when it was not returned. */
export function readCount(value) {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function typeLabel(t, type) {
    if (!type) return null;
    return SITEMAP_TYPES.includes(type) ? t(`tools.sitemap_status.types.${type}`) : type;
}

export function contentTypeLabel(t, type) {
    if (!type) return t('tools.gsc.not_returned');
    return CONTENT_TYPES.includes(type) ? t(`tools.sitemap_status.content_types.${type}`) : type;
}

function flag(t, value) {
    if (value === undefined || value === null) return null;
    return t(value ? 'tools.sitemap_status.yes' : 'tools.sitemap_status.no');
}

/** The last segment of a sitemap's path — how block 04 names it — or the path itself. */
export function sitemapName(path) {
    try {
        const url = new URL(path);
        return url.pathname.split('/').filter(Boolean).pop() || url.hostname;
    } catch (err) {
        return path;
    }
}

/** Block 01 (and an index's children) — one row per returned record. */
export function recordRows(t, sitemaps) {
    return (sitemaps ?? []).map((sitemap) => {
        const warnings = readCount(sitemap.warnings);
        const errors = readCount(sitemap.errors);

        return {
            id: sitemap.path,
            path: sitemap.path,
            type: sitemap.isSitemapsIndex
                ? t('tools.sitemap_status.types.index')
                : typeLabel(t, sitemap.type),
            lastSubmitted: formatTime(sitemap.lastSubmitted),
            lastDownloaded: formatTime(sitemap.lastDownloaded),
            pending: flag(t, sitemap.isPending),
            warnings,
            errors,
            isPending: sitemap.isPending === true,
            isIndex: sitemap.isSitemapsIndex === true,
            hasIssues: (warnings ?? 0) > 0 || (errors ?? 0) > 0,
            sitemap,
        };
    });
}

/** The record filter, applied to rows in memory — switching it costs no request. */
export function filterRecords(rows, filter) {
    switch (filter) {
        case 'issues':
            return rows.filter((row) => row.hasIssues);
        case 'pending':
            return rows.filter((row) => row.isPending);
        case 'indexes':
            return rows.filter((row) => row.isIndex);
        default:
            return rows;
    }
}

/** Block 02 — the selected record's fields: readable value beside the value Google returned. */
export function detailRows(t, sitemap) {
    const raw = (value) => (value === undefined || value === null ? null : String(value));
    const row = (id, field, reading, extra = {}) => ({
        id,
        field: t(`tools.sitemap_status.details.${id}`),
        reading,
        raw: raw(sitemap[field]),
        ...extra,
    });

    const warnings = readCount(sitemap.warnings);
    const errors = readCount(sitemap.errors);

    return [
        row('path', 'path', sitemap.path, { isUrl: true }),
        row('type', 'type', typeLabel(t, sitemap.type)),
        row('pending', 'isPending', flag(t, sitemap.isPending)),
        row('index', 'isSitemapsIndex', flag(t, sitemap.isSitemapsIndex)),
        row('last_submitted', 'lastSubmitted', formatTime(sitemap.lastSubmitted)),
        row('last_downloaded', 'lastDownloaded', formatTime(sitemap.lastDownloaded)),
        row('warnings', 'warnings', formatCount(warnings)),
        row('errors', 'errors', formatCount(errors), { tone: errors > 0 ? 'loss' : '' }),
    ];
}

/** Block 03 — the selected record's submitted entries, one row per content type returned. */
export function contentRows(t, sitemap) {
    return (sitemap?.contents ?? []).map((content, index) => {
        const type = contentTypeLabel(t, content.type);

        return {
            id: `${content.type ?? 'type'}-${index}`,
            type,
            submitted: readCount(content.submitted),
            interpretation: t('tools.sitemap_status.contents.interpretation', { type }),
        };
    });
}

/**
 * Block 04 — what the returned records flag, and the scope the page holds them under.
 *
 * Warnings and errors are counts. The Sitemaps API does not say what they are, and this block does
 * not pretend to: it names the record and the count, and says the descriptions are not supplied.
 */
export function issueRows(t, rows, { fetchedAt, filterLabel, shown }) {
    const notReturned = t('tools.gsc.not_returned');
    const count = (value) => (value === null ? notReturned : formatCount(value));
    const flagged = [];

    rows.forEach((row) => {
        const scope = sitemapName(row.path);

        if (row.hasIssues) {
            flagged.push({
                id: `issues-${row.path}`,
                scope,
                evidence: t('tools.sitemap_status.issues.counts', {
                    warnings: count(row.warnings),
                    errors: count(row.errors),
                }),
                tone: row.errors > 0 ? 'loss' : '',
            });
        }

        if (row.isPending) {
            flagged.push({
                id: `pending-${row.path}`,
                scope,
                evidence: row.lastDownloaded
                    ? t('tools.sitemap_status.issues.pending')
                    : t('tools.sitemap_status.issues.pending_not_downloaded'),
            });
        } else if (row.warnings === null || row.errors === null) {
            flagged.push({
                id: `missing-${row.path}`,
                scope,
                evidence: t('tools.sitemap_status.issues.counts_not_returned'),
            });
        }
    });

    if (rows.length && !flagged.length) {
        flagged.push({
            id: 'clean',
            scope: t('tools.sitemap_status.issues.all_records'),
            evidence: t('tools.sitemap_status.issues.none'),
        });
    }

    return [
        ...flagged,
        {
            id: 'returned',
            scope: t('tools.sitemap_status.issues.returned_label'),
            evidence: t('tools.sitemap_status.issues.returned', { count: rows.length }),
        },
        {
            id: 'filter',
            scope: t('tools.sitemap_status.issues.filter_label'),
            evidence: t('tools.sitemap_status.issues.filter', { filter: filterLabel, shown }),
        },
        {
            id: 'fetched',
            scope: t('tools.sitemap_status.issues.fetched_label'),
            evidence: formatTime(fetchedAt),
        },
        {
            id: 'export',
            scope: t('tools.sitemap_status.issues.export_label'),
            evidence: t('tools.sitemap_status.issues.export'),
        },
    ];
}
